import React from 'react';
import { Box, Chip } from '@mui/material';
import { AutoAwesome as AiIcon } from '@mui/icons-material';
import { NodeViewWrapper, ReactNodeViewProps } from '@tiptap/react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

/**
 * Renders an AI-generated (or student-written) text block within the canvas, with a clear
 * visual distinction between the two (spec 044 FR-024/SC-007) — a labeled badge plus a
 * tinted background/left border, not just a subtle color shift, so it reads unambiguously
 * "at a glance" as the spec requires.
 *
 * Spec 046 (FR-020, research Decision 5): content renders as markdown with KaTeX maths —
 * `$...$` / `$$...$$` from the AI displays as proper typeset notation, matching the
 * repo-wide RichContent pattern.
 */
const AiTextBlockNode: React.FC<ReactNodeViewProps> = ({ node }) => {
  const { text, origin } = node.attrs as { text: string; origin: 'AI' | 'STUDENT' };
  const isAi = origin === 'AI';

  return (
    <NodeViewWrapper as="div">
      <Box
        sx={{
          my: 1,
          p: 1.5,
          borderRadius: 1,
          bgcolor: isAi ? 'action.hover' : 'transparent',
          borderLeft: '3px solid',
          borderColor: isAi ? 'primary.main' : 'divider',
        }}
        data-origin={origin}
      >
        {isAi && (
          <Chip
            icon={<AiIcon sx={{ fontSize: '14px !important' }} />}
            label="AI"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.65rem', mb: 0.75 }}
          />
        )}
        <Box sx={{ lineHeight: 1.7, '& p': { my: 0.5 }, '& p:first-of-type': { mt: 0 }, '& p:last-of-type': { mb: 0 } }}>
          <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
            {text || ''}
          </ReactMarkdown>
        </Box>
      </Box>
    </NodeViewWrapper>
  );
};

export default AiTextBlockNode;
